import {HttpClient} from "@angular/common/http";
import {inject, Injectable} from "@angular/core";
import {Observable} from "rxjs";
import {map} from "rxjs/operators";
import {OrgDto} from "../dto/org.dto";
import {NoticeDto, NoticeRemindDto} from "../dto/notice.dto";
import {WrapperDto} from "../dto/wrapper.dto";
import {UrlDto} from "../item/url.dto";
import {UrlBuilder} from "../item/url.item";

/**
 * Die Pflichtmitteilungen im Dashboard.
 *
 * ⛔ **Ohne Zwischenspeicher.** Eine Mitteilung, die nach der Unterzeichnung noch aus dem Speicher käme, forderte
 * zu etwas auf, das bereits geschehen ist — und der stündliche Wiederaufruf aus Punkt 13 des Zustimmungsplans
 * würde sinnlos.
 */
@Injectable({providedIn: 'root'})
export class NoticeRepo {

  private readonly open: string = '/v1/org/{orgId}/notice';
  private readonly acknowledge: string = '/v1/org/{orgId}/notice/{noticeId}/acknowledge';
  private readonly sign: string = '/v1/org/{orgId}/notice/{noticeId}/sign';
  private readonly remind: string = '/v1/org/{orgId}/notice/{noticeId}/remind';

  private readonly http: HttpClient = inject(HttpClient);

  public getOpen(org: OrgDto): Observable<WrapperDto<NoticeDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.open).addParam('orgId', org.id).build();
    return this.http.get<WrapperDto<NoticeDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  /** Kenntnisnahme — oder Widerspruch, dann mit Begründung. */
  public acknowledgeNotice(org: OrgDto, noticeId: string, objection: boolean, reason?: string): Observable<WrapperDto<void>> {
    const builder = UrlBuilder.builder().addPath(this.acknowledge)
      .addParam('orgId', org.id).addParam('noticeId', noticeId)
      .addQueryParams('objection', String(objection));
    if (reason) {
      builder.addQueryParams('reason', reason);
    }
    const url: UrlDto = builder.build();
    return this.http.post<WrapperDto<void>>(url.getUrl(), {}, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  /** „Administrator erinnern" — `core` adressiert; der Browser kennt die Adressen nicht. */
  public remindSignatories(org: OrgDto, noticeId: string): Observable<WrapperDto<NoticeRemindDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.remind)
      .addParam('orgId', org.id).addParam('noticeId', noticeId).build();
    return this.http.post<WrapperDto<NoticeRemindDto>>(url.getUrl(), {}, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  /** Unterzeichnen — ein Aufruf, vier Nachweiszeilen im Backend. */
  public signNotice(org: OrgDto, noticeId: string, authorizedConfirmed: boolean): Observable<WrapperDto<void>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.sign)
      .addParam('orgId', org.id).addParam('noticeId', noticeId)
      .addQueryParams('authorizedConfirmed', String(authorizedConfirmed)).build();
    return this.http.post<WrapperDto<void>>(url.getUrl(), {}, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }
}
